/**
 * Bonificação Router Hooks
 *
 * Custom hooks for the bonus router endpoints.
 * Backend source: packages/serverless/api/src/handlers/monumenta-trpc/bonus/router.ts
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { get, post } from 'aws-amplify/api'

import { getConsolidatedData } from '@/api/sig-data'

// --- Types ---

export interface ProaStatus {
  meta: number
  atingido: number
  passed: boolean
}

export interface BonusAssessmentSummary {
  assessmentId: string
  employeeId: string
  employeeName: string
  department: string
  role: string
  pictureUrl?: string
  status: 'rascunho' | 'em_apuracao' | 'validacao_rh' | 'finalizado'
  scoreGeral?: number
  totalSalarios?: number
}

export interface GetDashboardInput {
  year: number
}

export interface GetDashboardOutput {
  proaStatus: ProaStatus
  stats: {
    total: number
    byStatus: {
      rascunho: number
      em_apuracao: number
      validacao_rh: number
      finalizado: number
    }
    avgBonus: number
  }
  assessments: BonusAssessmentSummary[]
}

/**
 * Custom fetch for tRPC query procedures (GET with input as query param).
 * tRPC v10 requires GET for queries, POST for mutations.
 */
async function customQuery(path: string, input: unknown): Promise<unknown> {
  const fullPath = path.startsWith('/monumenta-trpc') ? path : `/monumenta-trpc${path}`
  const inputParam = input ? `?input=${encodeURIComponent(JSON.stringify(input))}` : ''
  const apiOperation = get({
    apiName: 'monumenta-trpc',
    path: `${fullPath}${inputParam}`,
  })

  const response = await apiOperation.response
  const body = await response.body.text()
  const parsed = JSON.parse(body)

  if (parsed.result?.data) {
    return parsed.result.data
  }
  if (parsed.error) {
    throw new Error(parsed.error.message || 'Unknown error')
  }
  return parsed
}

/**
 * Custom fetch for tRPC mutation procedures (POST with body).
 */
async function customMutation(path: string, input: unknown): Promise<unknown> {
  const fullPath = path.startsWith('/monumenta-trpc') ? path : `/monumenta-trpc${path}`
  const apiOperation = post({
    apiName: 'monumenta-trpc',
    path: fullPath,
    options: {
      body: input as Record<string, unknown>,
    } as Parameters<typeof post>[0]['options'],
  })

  const response = await apiOperation.response
  const body = await response.body.text()
  const parsed = JSON.parse(body)

  if (parsed.result?.data) {
    return parsed.result.data
  }
  if (parsed.error) {
    throw new Error(parsed.error.message || 'Unknown error')
  }
  return parsed
}

/**
 * Hook for fetching the bonus dashboard data for a given year
 */
export function useBonusDashboard(
  year: number,
  options?: { enabled?: boolean }
) {
  return useQuery<GetDashboardOutput>({
    queryKey: ['bonus', 'getDashboard', { year }],
    queryFn: async () => {
      const result = await customQuery('/bonus/getDashboard', { year })
      return result as GetDashboardOutput
    },
    enabled: options?.enabled ?? true,
  })
}

// --- Assessment Types ---

export interface BonusAssessmentTemplate {
  templateId: string
  name: string
  year: number
  categorias: import('@/components/performance/bonificacao/types').BonusCategoria[]
}

export interface BonusAssessment {
  assessmentId: string
  tenantId: string
  employeeId: string
  templateId: string
  year: number
  ciclo: string
  status: 'rascunho' | 'em_apuracao' | 'validacao_rh' | 'finalizado'
  dados: Record<string, import('@/components/performance/bonificacao/types').IndicadorDados>
  publicados: Record<string, boolean>
  template: BonusAssessmentTemplate
  employee?: {
    name: string
    department?: string
    role?: string
    salary: number
    pictureUrl?: string
  }
  createdAt: string
  updatedAt: string
  finalizedAt?: string
  /** Full OKR list for Category 3 — dynamic length */
  _okrs?: import('@/components/performance/bonificacao/types').OKRItem[]
}

export interface BonusResult {
  categorias: import('@/components/performance/bonificacao/types').CategoriaResult[]
  scoreGeral: number
  totalSalarios: number
  totalMonetario: number
  gateBlocked: boolean
  gates: Record<string, import('@/components/performance/bonificacao/types').GateStatus>
  calculatedAt: string
}

// --- Assessment Hooks ---

export function useBonusAssessment(
  assessmentId: string | null,
  options?: { enabled?: boolean }
) {
  return useQuery<BonusAssessment>({
    queryKey: ['bonus', 'getAssessment', { assessmentId }],
    queryFn: async () => {
      const result = await customQuery('/bonus/getAssessment', { assessmentId }) as BonusAssessment

      // If backend didn't embed template, fetch it separately
      if (!result.template && result.templateId) {
        try {
          const template = await customQuery('/bonus/getTemplate', { templateId: result.templateId }) as BonusAssessmentTemplate & { name: string; year: number; categorias: unknown[] }
          if (template) {
            result.template = {
              templateId: result.templateId,
              name: template.name,
              year: template.year,
              categorias: template.categorias as BonusAssessmentTemplate['categorias'],
            }
          }
        } catch {
          // Template fetch failed — continue without it
          console.warn('[useBonusAssessment] Failed to fetch template', result.templateId)
        }
      }

      return result
    },
    enabled: (options?.enabled ?? true) && !!assessmentId,
  })
}

export function useBonusCalculate(
  assessmentId: string | null,
  options?: { enabled?: boolean }
) {
  return useQuery<BonusResult>({
    queryKey: ['bonus', 'calculate', { assessmentId }],
    queryFn: async () => {
      const result = await customQuery('/bonus/calculate', { assessmentId })
      return result as BonusResult
    },
    enabled: (options?.enabled ?? true) && !!assessmentId,
  })
}

export function useBonusSimulate(
  assessmentId: string | null,
  options?: { enabled?: boolean }
) {
  return useQuery<BonusResult>({
    queryKey: ['bonus', 'simulate', { assessmentId }],
    queryFn: async () => {
      const result = await customQuery('/bonus/simulate', { assessmentId })
      return result as BonusResult
    },
    enabled: (options?.enabled ?? true) && !!assessmentId,
  })
}

export function useAdvanceStatus() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (assessmentId: string) => {
      return customMutation('/bonus/advanceStatus', { assessmentId })
    },
    onSuccess: (_data, assessmentId) => {
      queryClient.invalidateQueries({ queryKey: ['bonus', 'getAssessment', { assessmentId }] })
      queryClient.invalidateQueries({ queryKey: ['bonus', 'calculate', { assessmentId }] })
      queryClient.invalidateQueries({ queryKey: ['bonus', 'getDashboard'] })
    },
  })
}

export function useRevertStatus() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (assessmentId: string) => {
      return customMutation('/bonus/revertStatus', { assessmentId })
    },
    onSuccess: (_data, assessmentId) => {
      queryClient.invalidateQueries({ queryKey: ['bonus', 'getAssessment', { assessmentId }] })
      queryClient.invalidateQueries({ queryKey: ['bonus', 'calculate', { assessmentId }] })
      queryClient.invalidateQueries({ queryKey: ['bonus', 'getDashboard'] })
    },
  })
}

// --- PROA % Calculation Hook ---

/**
 * Computes % PROA by combining:
 * - PROA LucroLiquido target from bonus getDashboard (DynamoDB)
 * - Actual LucroLiquido from DRE consolidated (SQL/Prisma)
 *
 * % PROA = actual LL / PROA LL target × 100
 */
export function useProaPercent(year: number) {
  return useQuery({
    queryKey: ['proa', 'percent', { year }],
    queryFn: async () => {
      // Fetch PROA target from getDashboard
      const dashData = await customQuery('/bonus/getDashboard', { year }) as GetDashboardOutput
      const proaTarget = dashData.proaStatus?.meta ?? 0

      // Fetch actual Lucro Líquido from DRE consolidated
      const dreData = await getConsolidatedData(String(year))
      const actualLL = (dreData?.data as Record<string, { real?: number }>)?.lucroLiquido?.real ?? 0

      if (proaTarget <= 0) {
        return { meta: proaTarget, atingido: 0, percent: 0, passed: false }
      }

      const percent = Math.round((actualLL / proaTarget) * 100)
      return {
        meta: proaTarget,
        atingido: actualLL,
        percent,
        passed: percent >= 100,
      }
    },
  })
}

// --- Template & Assessment Creation Hooks ---

export interface CreateTemplateInput {
  name: string
  description?: string
  year: number
  isActive?: boolean
  categorias: import('@/components/performance/bonificacao/types').BonusCategoria[]
}

export interface BonusTemplateListItem {
  templateId: string
  name: string
  year: number
  isActive: boolean
  categorias: import('@/components/performance/bonificacao/types').BonusCategoria[]
  createdAt: string
}

export interface CreateAssessmentInput {
  employeeId: string
  templateId: string
  year: number
}

export function useCreateTemplate() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: CreateTemplateInput) => {
      const result = await customMutation('/bonus/createTemplate', input)
      return result as { templateId: string }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bonus', 'listTemplates'] })
    },
  })
}

export function useListTemplates(year: number, options?: { enabled?: boolean }) {
  return useQuery<BonusTemplateListItem[]>({
    queryKey: ['bonus', 'listTemplates', { year }],
    queryFn: async () => {
      const result = await customQuery('/bonus/listTemplates', { year })
      return result as BonusTemplateListItem[]
    },
    enabled: options?.enabled ?? true,
  })
}

export function useCreateAssessment() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: CreateAssessmentInput) => {
      const result = await customMutation('/bonus/createAssessment', input)
      return result as { assessmentId: string }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bonus', 'getDashboard'] })
    },
  })
}

export function useDeleteAssessment() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (assessmentId: string) => {
      return customMutation('/bonus/deleteAssessment', { assessmentId })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bonus', 'getDashboard'] })
    },
  })
}

// --- GAP 4: Publish Control Hooks ---

export function useToggleCategoryPublish() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (payload: {
      assessmentId: string
      categoriaId: string
      publicado: boolean
    }) => {
      return customMutation('/bonus/toggleCategoryPublish', payload)
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['bonus', 'getAssessment', { assessmentId: variables.assessmentId }],
      })
      queryClient.invalidateQueries({ queryKey: ['bonus', 'getDashboard'] })
    },
  })
}

export function usePublishAll() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (payload: { assessmentId: string }) => {
      return customMutation('/bonus/publishAll', payload)
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['bonus', 'getAssessment', { assessmentId: variables.assessmentId }],
      })
      queryClient.invalidateQueries({ queryKey: ['bonus', 'getDashboard'] })
    },
  })
}

export function useUpdateIndicador() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (payload: {
      assessmentId: string
      indicadorId: string
      dados: Partial<import('@/components/performance/bonificacao/types').IndicadorDados>
    }) => {
      return customMutation('/bonus/updateIndicador', payload)
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['bonus', 'getAssessment', { assessmentId: variables.assessmentId }],
      })
      queryClient.invalidateQueries({
        queryKey: ['bonus', 'calculate', { assessmentId: variables.assessmentId }],
      })
    },
  })
}

// --- Evidence Upload/Download Hooks ---

interface GenerateEvidenceUploadUrlInput {
  assessmentId: string
  indicadorId: string
  fileName: string
  fileSize: number
  mimeType: string
}

interface EvidenceUploadUrlOutput {
  signedUrl: string
  evidenceId: string
  s3Key: string
  expiresIn: number
}

export function useGenerateEvidenceUploadUrl() {
  return useMutation({
    mutationFn: async (input: GenerateEvidenceUploadUrlInput) => {
      const result = await customMutation('/bonus/generateEvidenceUploadUrl', input)
      return result as EvidenceUploadUrlOutput
    },
  })
}

interface RegisterEvidenceInput {
  assessmentId: string
  indicadorId: string
  evidenceId: string
  s3Key: string
  fileName: string
  fileSize: number
  mimeType: string
}

export function useRegisterEvidence() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: RegisterEvidenceInput) => {
      return customMutation('/bonus/registerEvidence', input)
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['bonus', 'getAssessment', { assessmentId: variables.assessmentId }],
      })
    },
  })
}

interface EvidenceDownloadUrlInput {
  assessmentId: string
  indicadorId: string
  evidenceId: string
}

interface EvidenceDownloadUrlOutput {
  downloadUrl: string
  expiresIn: number
}

export function useGenerateEvidenceDownloadUrl() {
  return useMutation({
    mutationFn: async (input: EvidenceDownloadUrlInput) => {
      const result = await customMutation('/bonus/generateEvidenceDownloadUrl', input)
      return result as EvidenceDownloadUrlOutput
    },
  })
}

interface RemoveEvidenceInput {
  assessmentId: string
  indicadorId: string
  evidenceId: string
}

export function useRemoveEvidence() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: RemoveEvidenceInput) => {
      return customMutation('/bonus/removeEvidence', input)
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['bonus', 'getAssessment', { assessmentId: variables.assessmentId }],
      })
    },
  })
}
